import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Row, Col, Layout } from "antd";
import api from "../../../axiosConfig";
import MovilesColumn from "./MovilesColumn";
import TrasladosColumn from "./TrasladosColumn";
import ZonasColumn from "./ZonasColumn";
import MenuContextualTraslado from "./MenuContextualTraslado";
import SnackbarMensaje from "./SnackbarMensaje";
import TrasladosFilter from "./TrasladosFilter";
import dayjs from "../../../utils/dayjs";
import debounce from "lodash.debounce";
import { useUser } from "../../../UserContext";
import useNativeWebSocket from "../../../hooks/useNativeWebSocket";

// Importación de estilos
import "./Coordinacion.css";
import { geoJsonToLatLng } from "../../../utils/coordinates";

const { Header, Content } = Layout;

// Componente de columna reutilizable para mantener consistencia visual
const ColumnContainer = React.memo(({ children, className }) => (
  <div className={`column-container ${className || ""}`}>{children}</div>
));

const Coordinacion = () => {
  const [moviles, setMoviles] = useState([]);
  const [searchTermMoviles, setSearchTermMoviles] = useState("");
  const [selectedMovil, setSelectedMovil] = useState(null); // Para filtro (desde TrasladosFilter)
  const [selectedMovilForAssignment, setSelectedMovilForAssignment] =
    useState(null); // Para selección de pre-asignación
  const [traslados, setTraslados] = useState([]);
  const [totalAbsoluto, setTotalAbsoluto] = useState(0); // Total absoluto sin filtros
  const [searchTermTraslados, setSearchTermTraslados] = useState("");
  const [selectedTraslado, setSelectedTraslado] = useState(null);
  const [zonas, setZonas] = useState([]);
  const [zonasPermitidas, setZonasPermitidas] = useState(null); // null = sin restricción, array = zonas permitidas
  const [loadingTraslados, setLoadingTraslados] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [selectedConfirmacion, setSelectedConfirmacion] = useState("");
  const [selectedValidacion, setSelectedValidacion] = useState("");
  const [selectedZona, setSelectedZona] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("daily");
  // Usar fechas locales sin conversión de timezone para evitar desfases
  const [fechaInicio, setFechaInicio] = useState(() => {
    const today = new Date();
    return dayjs(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`);
  });
  const [fechaFin, setFechaFin] = useState(() => {
    const today = new Date();
    return dayjs(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`);
  });
  const [selectedTraslados, setSelectedTraslados] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(null);

  // NUEVOS para filtro por Departamento y Ciudad
  const [selectedDepartamento, setSelectedDepartamento] = useState(null);
  const [selectedCiudad, setSelectedCiudad] = useState(null);
  // Nombres de departamento y ciudad para filtros WebSocket
  const [selectedDepartamentoNombre, setSelectedDepartamentoNombre] = useState(null);
  const [selectedCiudadNombre, setSelectedCiudadNombre] = useState(null);

  const [selectedCopago, setSelectedCopago] = useState([
    "con_copago",
    "sin_copago",
  ]); // Estado inicial con ambos valores

  // filtro de hora
  const [selectedHour, setSelectedHour] = useState(null);

  // filtro de EPS
  const [selectedEps, setSelectedEps] = useState("");

  // filtro de estado de vehículo (por defecto Activo)
  const [selectedEstadoVehiculo, setSelectedEstadoVehiculo] = useState("Activo");

  // Estado de observaciones removido - ahora viene del backend optimizado

  const { user } = useUser();

  // Memoizar la conversión de código de vehículo a ID para evitar recálculos innecesarios
  const selectedVehicleId = useMemo(() => {
    return selectedMovil ? selectedMovil.id : null;
  }, [selectedMovil]);

  // Cargar vehículos y conductores
  const fetchMovilesYConductores = useCallback(async () => {
    try {
      const responseMoviles = await api.get("/vehiculos/codigo-placa");
      const movilesData = responseMoviles.data;
      const responseConductores = await api.get(
        "/vehiculo-conductores/basic-info"
      );
      const conductoresData = responseConductores.data;
      const updatedMoviles = movilesData.map((movil) => {
        const conductorAsignado = conductoresData.find(
          (conductor) =>
            conductor.vehiculo_id === movil.id && conductor.es_principal
        );
        return {
          ...movil,
          Conductor: conductorAsignado ? conductorAsignado.Conductor : null,
        };
      });
      setMoviles(updatedMoviles);
    } catch (error) {
      console.error("Error al cargar vehículos y conductores:", error);
    }
  }, []);

  // Cargar traslados con filtros inteligentes (como Centro de Control)
  const fetchTraslados = useCallback(
    async (specificFilters = null) => {
      try {
        setLoadingTraslados(true);

        // Construir parámetros de filtros

        const params = {
          fechaInicio: fechaInicio?.format("YYYY-MM-DD"),
          fechaFin: fechaFin?.format("YYYY-MM-DD"),
          // Enviar filtros al backend para consulta optimizada
          searchQuery: searchTermTraslados || "",
          zona: selectedZona || "",
          status:
            selectedStatuses.length > 0 && selectedStatuses.length < 11
              ? selectedStatuses
              : [],
          seguimientoStatus:
            selectedValidacion !== "Todos" ? selectedValidacion : "",
          confirmacionStatus:
            selectedConfirmacion !== "Todos" ? selectedConfirmacion : "",
          copago: selectedCopago.length < 2 ? selectedCopago : [],
          movil: selectedVehicleId ? Number(selectedVehicleId) : null,
          // Filtro de EPS
          eps: selectedEps || "",
          // Nuevos filtros de departamento, ciudad y hora - solo aplicar si ambos están seleccionados
          departamento:
            selectedDepartamento && selectedCiudad
              ? selectedDepartamento
              : null,
          ciudad:
            selectedDepartamento && selectedCiudad ? selectedCiudad : null,
          startTime: selectedHour
            ? selectedHour.toTimeString().slice(0, 5)
            : null,
          endTime: selectedHour
            ? selectedHour.toTimeString().slice(0, 5)
            : null,
          // Límite inteligente: sin filtros = 1000, con filtros = ilimitado
          limit: 999999, // Sin límite
          includeObservacionesCount: true,
          ...specificFilters,
        };


        const response = await api.get("/traslados", { params });
        const {
          rows: trasladosData,
          totalAbsoluto: responseTotalAbsoluto = 0,
        } = response.data;

        // Ya no necesitamos consulta adicional de observaciones
        // El backend ahora incluye observaciones_count optimizado

        // Actualizar estados
        setTraslados(trasladosData);
        setTotalAbsoluto(responseTotalAbsoluto);
      } catch (error) {
        console.error("Error al cargar los traslados:", error);
      } finally {
        setLoadingTraslados(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      fechaInicio,
      fechaFin,
      searchTermTraslados,
      selectedMovil,
      selectedConfirmacion,
      selectedValidacion,
      selectedZona,
      selectedStatuses,
      selectedDepartamento,
      selectedCiudad,
      selectedCopago,
      selectedHour,
      selectedVehicleId,
      selectedEps,
    ]
  );

  // Función de refresh simplificada para tiempo real
  const refreshTraslados = useCallback(async () => {
    try {
      // Construir parámetros actuales sin mostrar loading
      const params = {
        fechaInicio: fechaInicio?.format("YYYY-MM-DD"),
        fechaFin: fechaFin?.format("YYYY-MM-DD"),
        searchQuery: searchTermTraslados || "",
        zona: selectedZona || "",
        status:
          selectedStatuses.length > 0 && selectedStatuses.length < 11
            ? selectedStatuses
            : [],
        seguimientoStatus:
          selectedValidacion !== "Todos" ? selectedValidacion : "",
        confirmacionStatus:
          selectedConfirmacion !== "Todos" ? selectedConfirmacion : "",
        copago: selectedCopago.length < 2 ? selectedCopago : [],
        movil: selectedVehicleId ? Number(selectedVehicleId) : null,
        // Filtro de EPS
        eps: selectedEps || "",
        // Nuevos filtros de departamento, ciudad y hora - solo aplicar si ambos están seleccionados
        departamento:
          selectedDepartamento && selectedCiudad ? selectedDepartamento : null,
        ciudad: selectedDepartamento && selectedCiudad ? selectedCiudad : null,
        startTime: selectedHour
          ? selectedHour.toTimeString().slice(0, 5)
          : null,
        endTime: selectedHour ? selectedHour.toTimeString().slice(0, 5) : null,
        limit: 999999, // Sin límite
        includeObservacionesCount: true,
      };

      const response = await api.get("/traslados", { params });
      const { rows: trasladosData, totalAbsoluto: responseTotalAbsoluto = 0 } =
        response.data;

      // Ya no necesitamos consulta adicional de observaciones
      // El backend ahora incluye observaciones_count optimizado

      // Actualizar estados
      setTraslados(trasladosData);
      setTotalAbsoluto(responseTotalAbsoluto);
    } catch (error) {
      console.error("Error en refresh automático:", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fechaInicio,
    fechaFin,
    searchTermTraslados,
    selectedMovil,
    selectedConfirmacion,
    selectedValidacion,
    selectedZona,
    selectedStatuses,
    selectedDepartamento,
    selectedCiudad,
    selectedCopago,
    selectedHour,
    selectedVehicleId,
    selectedEps,
  ]);


  // Preparar filtros para el hook WebSocket
  const filtrosWebSocket = useMemo(() => ({
    selectedZona,
    selectedStatuses,
    selectedValidacion,
    selectedConfirmacion,
    selectedCopago,
    selectedVehicleId,
    selectedDepartamento,
    selectedCiudad,
    // Nombres de departamento y ciudad para validación WebSocket
    selectedDepartamentoNombre,
    selectedCiudadNombre,
    selectedHour,
    searchTermTraslados,
    zonasPermitidas, // Lista de nombres de zonas permitidas para el usuario
    selectedEps // Filtro de EPS
  }), [
    selectedZona,
    selectedStatuses,
    selectedValidacion,
    selectedConfirmacion,
    selectedCopago,
    selectedVehicleId,
    selectedDepartamento,
    selectedCiudad,
    selectedDepartamentoNombre,
    selectedCiudadNombre,
    selectedHour,
    searchTermTraslados,
    zonasPermitidas,
    selectedEps
  ]);

  // Hook para manejo de WebSocket nativo con validación de fechas y filtros
  useNativeWebSocket(traslados, setTraslados, fechaInicio, fechaFin, filtrosWebSocket);

  useEffect(() => {
    const fetchZonas = async () => {
      try {
        const response = await api.get("/traslados/zonas-disponibles");
        setZonas(response.data.zonas || []);
        // Guardar los nombres de las zonas permitidas para filtrar WebSocket
        setZonasPermitidas(response.data.zonas_nombres || null);
      } catch (error) {
        console.error("Error al cargar zonas:", error);
        setZonas([]);
      }
    };
    fetchZonas();
  }, []);

  // La lista de departamentos y ciudades se gestionan en TrasladosFilter

  const debouncedFetchTraslados = useMemo(
    () => debounce(fetchTraslados, 300),
    [fetchTraslados]
  );

  useEffect(() => {
    fetchMovilesYConductores();
    debouncedFetchTraslados();
    return () => {
      debouncedFetchTraslados.cancel();
    };
  }, [fetchMovilesYConductores, debouncedFetchTraslados]);

  // Efecto para observar cambios en selectedMovil y disparar búsqueda
  useEffect(() => {
    if (moviles.length > 0) {
      // Usar refreshTraslados en lugar de fetchTraslados para mantener filtros consistentes
      const debouncedRefreshTraslados = debounce(refreshTraslados, 300);
      debouncedRefreshTraslados();
      return () => {
        debouncedRefreshTraslados.cancel();
      };
    }
  }, [selectedMovil, moviles.length, refreshTraslados]);

  const filteredMoviles = useMemo(() => {
    if (!searchTermMoviles) return moviles;
    const term = searchTermMoviles.toLowerCase();
    return moviles.filter(
      (movil) =>
        movil.codigo.toLowerCase().includes(term) ||
        movil.placa.toLowerCase().includes(term)
    );
  }, [moviles, searchTermMoviles]);

  // Los filtros ahora se aplican en el backend, no necesitamos filtrado en frontend
  const filteredTraslados = traslados;

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    const now = dayjs();
    let startDate, endDate;
    switch (period) {
      case "currentWeek":
        startDate = now.startOf("week");
        endDate = now.endOf("week");
        break;
      case "previousWeek":
        startDate = now.subtract(1, "week").startOf("week");
        endDate = now.subtract(1, "week").endOf("week");
        break;
      case "currentMonth":
        startDate = now.startOf("month");
        endDate = now.endOf("month");
        break;
      case "previousMonth":
        startDate = now.subtract(1, "month").startOf("month");
        endDate = now.subtract(1, "month").endOf("month");
        break;
      case "daily":
      default:
        startDate = now.startOf("day");
        endDate = now.endOf("day");
        break;
    }
    setFechaInicio(startDate);
    setFechaFin(endDate);
  };

  const handlePrevDay = () => {
    if (selectedPeriod === "daily") {
      setFechaInicio((prev) => prev.subtract(1, "day").startOf("day"));
      setFechaFin((prev) => prev.subtract(1, "day").endOf("day"));
    }
  };

  const handleNextDay = () => {
    if (selectedPeriod === "daily") {
      setFechaInicio((prev) => prev.add(1, "day").startOf("day"));
      setFechaFin((prev) => prev.add(1, "day").endOf("day"));
    }
  };

  const handleDateChange = (date) => {
    if (date && selectedPeriod === "daily") {
      setFechaInicio(date.startOf("day"));
      setFechaFin(date.endOf("day"));
    }
  };

  const handleContextMenu = (event, traslado) => {
    event.preventDefault();
    setSelectedTraslado(traslado);
    setContextMenu(
      contextMenu === null
        ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 }
        : null
    );
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleOptionClick = (option) => {
    if (!selectedTraslado) return;
    if (option === "copyPhone") {
      handleCopyToClipboard(selectedTraslado?.numero_contacto_paciente);
    } else if (option === "copyResponsiblePhone") {
      // Compatibilidad con el campo legacy
      handleCopyToClipboard(selectedTraslado?.numero_contacto_responsable);
    } else if (option.startsWith("copyResponsiblePhone:")) {
      // Nuevo formato: copyResponsiblePhone:PHONE_NUMBER
      const phone = option.split(":")[1];
      handleCopyToClipboard(phone);
    }
  };

  const handleCopyToClipboard = (phone) => {
    if (phone) {
      navigator.clipboard
        .writeText(phone)
        .then(() => {
          setSnackbarMessage(`Teléfono ${phone} copiado al portapapeles`);
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
        })
        .catch((err) => {
          console.error("Error al copiar al portapapeles:", err);
          setSnackbarMessage("Error al copiar el teléfono.");
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        });
    } else {
      setSnackbarMessage("No hay número de teléfono para copiar.");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
    }
    handleCloseContextMenu();
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const actualizarTraslado = (trasladoId, nuevosDatos) => {
    setTraslados((prev) =>
      prev.map((t) => (t.id === trasladoId ? { ...t, ...nuevosDatos } : t))
    );
  };

  const actualizarTrasladosEnEstado = (
    prevTraslados,
    trasladosActualizados
  ) => {
    return prevTraslados.map((traslado) => {
      const actualizado = trasladosActualizados.find(
        (t) => t.id === traslado.id
      );
      if (actualizado) {
        return {
          ...traslado,
          ...actualizado,
          nombre_vehiculo:
            actualizado.nombre_vehiculo || traslado.nombre_vehiculo,
          placa: actualizado.placa || traslado.placa,
          conductor_id: actualizado.conductor_id || traslado.conductor_id,
          nombre_conductor:
            actualizado.nombre_conductor || traslado.nombre_conductor,
        };
      }
      return traslado;
    });
  };

  const handleAsignarMovil = useCallback((trasladoId, nuevosDatos) => {
    setTraslados((prevTraslados) =>
      prevTraslados.map((traslado) =>
        traslado.id === trasladoId ? { ...traslado, ...nuevosDatos } : traslado
      )
    );
  }, []);

  // Actualizar origen - destino: limpiar la selección y recargar la lista con los datos nuevos
  const handleOrigenDestinoActualizado = () => {
    setSelectedTraslados([]);
    setSelectedStatus(null);
    fetchTraslados();
  };

  const handleAsignarMultiple = async (trasladosIds, status) => {
    let endpoint = "";
    let statusToAssign = "";

    if (status === "Programado") {
      if (!selectedMovilForAssignment) {
        setSnackbarMessage(
          "Debe seleccionar un móvil para pre-asignar traslados."
        );
        setSnackbarSeverity("warning");
        setSnackbarOpen(true);
        return;
      }
      endpoint = "/traslados/asignar-multiple";
      statusToAssign = "Pre-Asignado";
    } else if (status === "Pre-Asignado") {
      endpoint = "/traslados/asignar-multiple-asignado";
      statusToAssign = "Asignado";
    } else {
      return;
    }

    try {
      const dataToSend = {
        traslados_ids: trasladosIds,
        usuario_id: user.id,
        status: statusToAssign,
      };

      if (status === "Programado") {
        dataToSend.vehiculo_id = selectedMovilForAssignment.id;
        dataToSend.nombre_vehiculo = selectedMovilForAssignment.codigo;
        dataToSend.placa = selectedMovilForAssignment.placa;
        dataToSend.conductor_id =
          selectedMovilForAssignment.Conductor?.id ?? null;
        dataToSend.nombre_conductor =
          selectedMovilForAssignment.Conductor?.nombre ?? null;
      }

      const response = await api.put(endpoint, dataToSend);
      const trasladosActualizados = response.data.traslados;
      const noAsignados = response.data.no_asignados || [];

      setTraslados((prev) =>
        actualizarTrasladosEnEstado(prev, trasladosActualizados)
      );

      if (noAsignados.length > 0) {
        // El servidor asigna cada traslado por separado: si alguno falla, los demás quedan asignados
        setSnackbarMessage(
          `${trasladosActualizados.length} traslados ${statusToAssign}. ${noAsignados.length} no se pudieron asignar (CS ${noAsignados
            .map((t) => t.id)
            .join(", ")}); intente de nuevo con ellos.`
        );
        setSnackbarSeverity("warning");
      } else {
        setSnackbarMessage(`Traslados ${statusToAssign} correctamente.`);
        setSnackbarSeverity("success");
      }
      setSnackbarOpen(true);

      setSelectedTraslados([]);
      setSelectedStatus(null);

      if (status === "Programado") {
        setSelectedMovilForAssignment(null);
      }
    } catch (error) {
      console.error(`Error al ${statusToAssign} los traslados:`, error);
      setSnackbarMessage(`Error al ${statusToAssign} los traslados.`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };


  const [filterChangeTrigger, setFilterChangeTrigger] = useState(0);
  const handleManualFilterChange = useCallback(() => {
    setFilterChangeTrigger((prev) => prev + 1);
  }, []);

  const handleCopagoFilter = useCallback((values) => {
    setSelectedCopago(values);
  }, []);

  // Función para manejar el cambio en el filtro de hora
  const handleHourFilter = useCallback((hour) => {
    setSelectedHour(hour);
  }, []);

  // Función para manejar el cambio en el filtro de EPS
  const handleEpsFilter = useCallback((eps) => {
    setSelectedEps(eps);
  }, []);

  // Función para manejar el cambio en el filtro de departamento
  const handleDepartamentoFilter = useCallback(
    (departamentoId, departamentoNombre = null) => {
      setSelectedDepartamento(departamentoId);
      setSelectedDepartamentoNombre(departamentoNombre);
      // Al cambiar departamento, limpiar ciudad seleccionada
      if (departamentoId !== selectedDepartamento) {
        setSelectedCiudad(null);
        setSelectedCiudadNombre(null);
      }
    },
    [selectedDepartamento]
  );

  // Función para manejar el cambio en el filtro de ciudad
  const handleCiudadFilter = useCallback((ciudadId, ciudadNombre = null) => {
    setSelectedCiudad(ciudadId);
    setSelectedCiudadNombre(ciudadNombre);
  }, []);

  const handleVehicleFilter = (vehicleCode) => {
    if (vehicleCode) {
      const movil = moviles.find((m) => m.codigo === vehicleCode);
      setSelectedMovil(movil || null);
    } else {
      setSelectedMovil(null);
    }
    // No llamar fetchTraslados directamente, el sistema de filtros manual se encargará
  };

  return (
    <Layout className="coordinacion-layout">
      <Header className="coordinacion-header">
        <TrasladosFilter
          onSearchTraslados={setSearchTermTraslados}
          onVehicleFilter={handleVehicleFilter}
          onConfirmacionFilter={setSelectedConfirmacion}
          onValidacionFilter={setSelectedValidacion}
          onZonaFilter={setSelectedZona}
          onStatusFilter={setSelectedStatuses}
          onDateChange={handleDateChange}
          onPrevDay={handlePrevDay}
          onNextDay={handleNextDay}
          onPeriodChange={handlePeriodChange}
          selectedPeriod={selectedPeriod}
          selectedDate={selectedPeriod === "daily" ? fechaInicio : null}
          moviles={moviles}
          zonas={zonas}
          onManualFilterChange={handleManualFilterChange}
          onDepartamentoFilter={handleDepartamentoFilter}
          onCiudadFilter={handleCiudadFilter}
          selectedCiudad={selectedCiudad}
          onCiudadesChange={() => {}}
          onCopagoFilter={handleCopagoFilter}
          onHourFilter={handleHourFilter}
          selectedHour={selectedHour}
          isLoading={loadingTraslados}
          onEpsFilter={handleEpsFilter}
          selectedEps={selectedEps}
        />
      </Header>

      <Content className="coordinacion-content">
        <Row gutter={[16, 16]} className="columns-row">
          {/* Columna de Móviles */}
          <Col xs={24} sm={24} md={6} lg={4} xl={4} className="column-wrapper">
            <ColumnContainer>
              <MovilesColumn
                moviles={filteredMoviles}
                searchTerm={searchTermMoviles}
                setSearchTerm={setSearchTermMoviles}
                selectedMovilId={selectedMovilForAssignment?.id}
                onSelectMovil={setSelectedMovilForAssignment}
                selectedEstadoVehiculo={selectedEstadoVehiculo}
                onEstadoVehiculoChange={setSelectedEstadoVehiculo}
              />
            </ColumnContainer>
          </Col>

          {/* Columna de Traslados (principal) */}
          <Col
            xs={24}
            sm={24}
            md={12}
            lg={16}
            xl={16}
            className="column-wrapper"
          >
            <ColumnContainer className="main-column">
              <TrasladosColumn
                traslados={filteredTraslados}
                handleContextMenu={handleContextMenu}
                onAsignarMovil={handleAsignarMovil}
                onAsignarMultiple={handleAsignarMultiple}
                selectedMovil={selectedMovilForAssignment}
                setSelectedMovil={setSelectedMovilForAssignment}
                loading={loadingTraslados}
                selectedTraslados={selectedTraslados}
                setSelectedTraslados={setSelectedTraslados}
                selectedStatus={selectedStatus}
                setSelectedStatus={setSelectedStatus}
                filterChangeTrigger={filterChangeTrigger}
                // observacionesCounts removido - ahora viene del backend
                totalAbsoluto={totalAbsoluto}
              />
            </ColumnContainer>
          </Col>

          {/* Columna de Zonas */}
          <Col xs={24} sm={24} md={6} lg={4} xl={4} className="column-wrapper">
            <ColumnContainer>
              <ZonasColumn traslados={filteredTraslados} />
            </ColumnContainer>
          </Col>
        </Row>

        {/* Componentes de UI adicionales */}
        {selectedTraslado && (
          <MenuContextualTraslado
            contextMenu={contextMenu}
            handleCloseContextMenu={handleCloseContextMenu}
            handleOptionClick={handleOptionClick}
            trasladoId={selectedTraslado.id}
            traslado={selectedTraslado}
            onEstadoCambiado={actualizarTraslado}
            origin={geoJsonToLatLng(selectedTraslado?.origen_coordenadas)}
            destination={geoJsonToLatLng(selectedTraslado?.destino_coordenadas)}
            selectedTraslados={selectedTraslados}
            onAsignarMultiple={handleAsignarMultiple}
            selectedMovil={selectedMovilForAssignment}
            selectedStatus={selectedStatus}
            onOrigenDestinoActualizado={handleOrigenDestinoActualizado}
          />
        )}

        <SnackbarMensaje
          snackbarOpen={snackbarOpen}
          handleSnackbarClose={handleSnackbarClose}
          snackbarMessage={snackbarMessage}
          severity={snackbarSeverity}
        />
      </Content>
    </Layout>
  );
};

export default React.memo(Coordinacion);
